import PropTypes from 'prop-types'
import clsx from 'clsx'
import { rgba } from 'polished'
import { useTheme } from '@emotion/react'
import { text } from 'chaoskit/src/assets/styles/utility'
import { Inline, ListItem } from 'chaoskit/src/components'

// Normalizes data
export const getDigits = (digits) => {
  // Add zero when needed
  if (digits < 10) {
    return `0${digits}`
  }

  return digits.toString()
}

export const createTimeObject = (time) => {
  const seconds = time
  const minutes = seconds / 60
  const hours = minutes / 60
  const days = hours / 24

  return {
    days: getDigits(Math.floor(days)),
    hours: getDigits(Math.floor(hours % 24)),
    minutes: getDigits(Math.floor(minutes % 60)),
    seconds: getDigits(Math.floor(seconds % 60)),
  }
}

export const CounterWrapper = ({
  children,
  className,
  groupCount,
  ...rest
}) => {
  return (
    <Inline
      css={{ textAlign: 'center' }}
      className={clsx('u-contrast', className)}
      {...rest}
    >
      {children}
    </Inline>
  )
}

CounterWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  groupCount: PropTypes.number.isRequired,
}

// Holds groups of digits
export const CounterGroup = ({ children, label, size = 'base', ...rest }) => {
  const theme = useTheme()

  return (
    <ListItem {...rest}>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: `repeat(2, ${theme.height[size]}px)`,
          gap: theme.space.small,
        }}
      >
        {children}
      </div>
      <div
        css={[
          text.heading(theme),
          {
            marginTop: theme.space.small,
            letterSpacing: theme.letterSpacing.base,
          },
        ]}
      >
        {label}
      </div>
    </ListItem>
  )
}

CounterGroup.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['base', 'small']),
}

// Digit display
const SpanDigit = ({ size = 'base', ...rest }) => {
  const theme = useTheme()

  return (
    <span
      css={[
        text.heading(theme),
        {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: rgba(theme.contrast.base, 0.25),
          width: '100%',
          height: '100%',
          lineHeight: `${theme.height[size] / 1.075}px`,

          fontSize: theme.height[size] / 1.25,
          borderRadius: theme.borderRadius.base,

          '&:first-of-type': {
            clipPath: 'polygon(0% 0%, 100% 0%, 100% 48%, 0% 48%)',
          },

          '&:last-of-type': {
            clipPath: 'polygon(0% 52%, 100% 52%, 100% 100%, 0% 100%)',
          },
        },
      ]}
      {...rest}
    />
  )
}

SpanDigit.propTypes = {
  size: PropTypes.oneOf(['base', 'small']),
}

// Holds each individual digit
export const CounterDigit = ({ digit, size = 'base', ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        position: 'relative',
        width: theme.height[size],
        height: theme.height[size],
        boxShadow: `0 0 0 2px ${rgba(theme.color.dark.base, 0.075)}`,
        borderRadius: theme.borderRadius.base,
      }}
      {...rest}
    >
      <SpanDigit size={size}>{digit}</SpanDigit>
      <SpanDigit size={size}>{digit}</SpanDigit>
    </div>
  )
}

CounterDigit.propTypes = {
  digit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  size: PropTypes.oneOf(['base', 'small']),
}
